import React from "react";
import StageNotCompletedNotice from "./StageNotCompletedNotice";

const StageIcon = ({ stroke = "#cbd5e1" }) => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
    <rect x="9" y="3" width="6" height="4" rx="1" />
    <line x1="9" y1="12" x2="15" y2="12" />
    <line x1="9" y1="16" x2="13" y2="16" />
  </svg>
);

const QUESTION_TYPE_ID_TO_LABEL = {
  "4c524f2f-48de-493e-894f-4f0bd931b4c9": "MCQ",
  "64468292-d741-4e52-893f-0846dd56830d": "Numeric",
  "4ffc711b-2f10-470b-b162-85f2a2f1e220": "Yes/No",
  "30afb159-85d7-490d-afcd-1af9497531ae": "Short answer",
};

const PRE_SCREENING_STAGE_ID = "3dab71cf-c680-4983-a109-1cff0953999b";

const getBadge = (score) => {
  if (score === null || score === undefined) return { label: "N/A", cls: "cpv2-pre__badge--average" };
  if (score < 50) return { label: "Weak", cls: "cpv2-pre__badge--weak" };
  if (score < 75) return { label: "Average", cls: "cpv2-pre__badge--average" };
  if (score < 90) return { label: "Good", cls: "cpv2-pre__badge--good" };
  return { label: "Excellent", cls: "cpv2-pre__badge--excellent" };
};

const getScoreColorCls = (score, max) => {
  if (!max) return "";
  const pct = (score / max) * 100;
  if (pct >= 100) return "cpv2-pre__score--full";
  if (pct >= 50) return "cpv2-pre__score--partial";
  return "cpv2-pre__score--zero";
};

const PreScreeningStage = ({
  candidate,
  preQualifyingReport,
  tabLoading,
  formatExpiry,
  isSharedReport,
  canAssignStage = true,
  setPreselectStage,
  setIsOpen,
}) => {
  if (tabLoading) {
    return (
      <div className="cpv2-loading" style={{ minHeight: 200 }}>
        <div className="spinner-border" />
      </div>
    );
  }

  const stages = candidate?.hiring_stages?.filter(
    (s) => s.hiring_stage_id === PRE_SCREENING_STAGE_ID,
  ) || [];
  const latestStage = stages.length > 0
    ? stages.reduce((a, b) => new Date(b.created_at) > new Date(a.created_at) ? b : a)
    : null;
  const status = (latestStage?.status?.name || "").trim().toLowerCase();

  const assignBtn = !isSharedReport && canAssignStage && (
    <button
      className="cpv2-pre__assign-btn"
      onClick={() => { setPreselectStage("Pre Screening"); setIsOpen(true); }}
    >
      Assign Pre-Screening Test
    </button>
  );

  if (status === "completed" || status === "not completed") {
    const isNotCompleted = status === "not completed";
    const report = preQualifyingReport?.[0];
    const evaluations = report?.evaluations || [];
    const totalScore = report?.total_score ?? null;
    const { label: badgeLabel, cls: badgeCls } = getBadge(totalScore);
    const stageEvaluationEnabled = report?.enable_evaluation ?? true;

    if (isNotCompleted && evaluations.length === 0) {
      return (
        <div className="cpv2-pre">
          <div className="cpv2-stage-empty">
            <StageIcon stroke="#fcd34d" />
            <p className="cpv2-stage-empty__msg cpv2-stage-empty__msg--warning">
              Pre-Screening not completed. The candidate did not submit any answers.
            </p>
            {assignBtn}
          </div>
        </div>
      );
    }

    return (
      <div className="cpv2-pre">
        {isNotCompleted && <StageNotCompletedNotice stageLabel="pre-screening" action={assignBtn} />}
        {evaluations.length > 0 ? (
          <>
            {stageEvaluationEnabled && (
              <div className="cpv2-pre__score-row">
                <div className="cpv2-pre__score-value">
                  {totalScore !== null ? Math.round(totalScore) : "--"}
                  <span> / 100</span>
                </div>
                <span className={`cpv2-pre__badge ${badgeCls}`}>{badgeLabel}</span>
              </div>
            )}
            <p className="cpv2-pre__meta">{evaluations.length} questions</p>

            <div className="cpv2-pre__table-header">
              <span>Question</span>
              {stageEvaluationEnabled && <span>Expected</span>}
              <span>Answer</span>
              {stageEvaluationEnabled && <span>Score</span>}
            </div>

            {evaluations.map((evalItem, idx) => {
              if (!evalItem.question) return null;
              const typeLabel = QUESTION_TYPE_ID_TO_LABEL[evalItem.question?.question_type_id] || "Unknown";
              const isMCQ = evalItem.question?.question_type_id === "4c524f2f-48de-493e-894f-4f0bd931b4c9";
              // MCQ correct answers are a list of strings; older questions stored a single string.
              const correctOptionTexts = []
                .concat(evalItem.question?.meta_data?.required_option_text || [])
                .filter(t => typeof t === "string" && t.trim())
                .map(t => t.trim().toLowerCase());
              const isMultiAnswer = correctOptionTexts.length > 1;
              const correctAnswer = evalItem.question?.meta_data?.required_answer_text
                || [].concat(evalItem.question?.meta_data?.required_option_text || []).filter(Boolean).join(", ")
                || null;
              const candidateAnswer = evalItem.answer_text || evalItem.selected_option?.option_text || null;
              const score = evalItem.score ?? 0;
              const maxScore = evalItem.question?.score ?? 0;
              const questionEvaluationEnabled = evalItem.question?.meta_data?.enable_evaluation ?? true;

              return (
                <div className="cpv2-pre__question-row" key={evalItem.id || idx}>
                  <div className="cpv2-pre__qtext">
                    <span className="type-label">{typeLabel}</span>
                    {evalItem.question?.question_text}
                  </div>
                  {stageEvaluationEnabled && (
                    <div className="cpv2-pre__expected">
                      {questionEvaluationEnabled ? (
                        isMCQ ? (
                          <>
                            <span className="cpv2-pre__mcq-hint">
                              {isMultiAnswer
                                ? `Multiple correct answers (${correctOptionTexts.length})`
                                : "Single correct answer"}
                            </span>
                            <ul className={`cpv2-pre__mcq-list${isMultiAnswer ? " is-multi" : ""}`}>
                              {evalItem.question?.options?.map((opt) => {
                                const isCorrectOpt = correctOptionTexts.includes(
                                  (opt.option_text || "").trim().toLowerCase()
                                );
                                return (
                                  <li
                                    key={opt.id}
                                    className={`cpv2-pre__mcq-opt${isCorrectOpt ? " is-correct" : ""}`}
                                  >
                                    <span className="cpv2-pre__mcq-marker" aria-hidden="true">
                                      {isCorrectOpt && (
                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                                          <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                      )}
                                    </span>
                                    <span className="cpv2-pre__mcq-text">{opt.option_text}</span>
                                  </li>
                                );
                              })}
                            </ul>
                          </>
                        ) : (
                          correctAnswer || "N/A"
                        )
                      ) : "-"}
                    </div>
                  )}
                  <div className="cpv2-pre__answer">{candidateAnswer || "N/A"}</div>
                  {stageEvaluationEnabled && (
                    <div className={`cpv2-pre__score ${getScoreColorCls(score, maxScore)}`}>
                      {questionEvaluationEnabled ? `${score}/${maxScore}` : "-"}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        ) : (
          <p style={{ color: "#6b7280", fontSize: 13 }}>
            Pre-Screening test not assigned or not completed yet
          </p>
        )}
      </div>
    );
  }

  if (status === "assigned" || status === "scheduled") {
    return (
      <div className="cpv2-pre">
        <p style={{ color: "#6b7280", fontSize: 13 }}>
          Pre-Screening test assigned but not completed yet. Expiry:{" "}
          {formatExpiry(latestStage?.expire_at)}
        </p>
      </div>
    );
  }

  if (status === "ongoing") {
    return (
      <div className="cpv2-pre">
        <p style={{ color: "#6b7280", fontSize: 13 }}>
          Pre-Screening test ongoing, not completed yet. Expiry:{" "}
          {formatExpiry(latestStage?.expire_at)}
        </p>
      </div>
    );
  }

  if (!status || status === "not scheduled") {
    return (
      <div className="cpv2-pre">
        <div className="cpv2-stage-empty">
          <StageIcon />
          <p className="cpv2-stage-empty__msg">No pre-screening test assigned yet</p>
          {assignBtn}
        </div>
      </div>
    );
  }

  if (status === "expired") {
    return (
      <div className="cpv2-pre">
        <div className="cpv2-stage-empty">
          <StageIcon stroke="#fcd34d" />
          <p className="cpv2-stage-empty__msg cpv2-stage-empty__msg--warning">Pre-Screening Expired</p>
          {assignBtn}
        </div>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="cpv2-pre">
        <div className="cpv2-stage-empty">
          <StageIcon stroke="#fca5a5" />
          <p className="cpv2-stage-empty__msg cpv2-stage-empty__msg--error">Pre-Screening Failed</p>
          {assignBtn}
        </div>
      </div>
    );
  }

  return (
    <div className="cpv2-pre">
      <p style={{ color: "#6b7280", fontSize: 13 }}>Pre-Screening sent to candidate</p>
    </div>
  );
};

export default PreScreeningStage;
